import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Modal, Text } from '@fragments';
import { IconBookmark, IconChevronLeft, IconChevronRight, IconDots, IconHistory, IconLayoutGrid, IconPencil, IconPlus, IconSearch, IconTrash, IconUsers, IconSparkles } from '@tabler/icons-react';
import ArtPreview from '../view/components/ArtPreview';
import useTemplateDocument, { loadTemplate } from './useTemplateDocument';
import TemplateDialog, { formatNameOf } from './TemplateDialog';
import { gsap, enter, reducedMotion } from '../view/motion';
import '../view/components/ToolPanel/styles.scss';
import './styles.scss';

const HOVER_DELAY_MS = 1500;
const normalize = value => (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const canHover = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;
const SOURCE_ICON = { past_design: IconHistory, personal: IconBookmark, community: IconUsers, ginastee: IconSparkles };
// Past designs and personal templates have their own sections, not the category rows.
const OWN_SOURCES = ['past_design', 'personal'];

export function Thumb({ template, fetch }) {
  const preview = template.previews?.[0]?.url;
  const { ref, document, status } = useTemplateDocument(preview ? null : template, fetch);
  const ratio = template.aspect_ratio ? template.aspect_ratio.replace(':', ' / ') : '4 / 5';
  return <span ref={ref} className="art-gallery__thumb" style={{ aspectRatio: ratio }}>
    {preview ? <img src={preview} alt="" loading="lazy" draggable={false} />
      : document ? <ArtPreview document={document} rounded={false} />
        : <span className={`art-gallery__thumb-empty ${status === 'error' ? '' : 'art-skeleton'}`} />}
  </span>;
}

// A card, and for a personal template its ⋯ menu (edit, delete), a sibling over the thumbnail.
function Card({ template, fetch, selected, onPick, onHover, onLeave, onEdit, onDelete }) {
  const { t } = useTranslation('artEditor');
  const byline = template.author?.handle ? `@${template.author.handle.replace('@', '')}` : template.author?.name || t(`source_${template.source}`);
  const SourceIcon = SOURCE_ICON[template.source] || IconSparkles;
  const actions = template.source === 'personal' && (onEdit || onDelete);
  return <div className="art-gallery__item">
    <button type="button" className={`art-gallery__card ${selected ? 'is-selected' : ''}`} aria-label={template.name} aria-pressed={selected}
      onClick={event => (canHover() ? onPick(template) : onHover(template, event.currentTarget, true))}
      onPointerEnter={event => event.pointerType === 'mouse' && onHover(template, event.currentTarget)}
      onPointerLeave={event => event.pointerType === 'mouse' && onLeave(template)}>
      <Thumb template={template} fetch={fetch} />
      {template.page_count > 1 && <span className="art-gallery__badge">{t('pageCount', { count: template.page_count })}</span>}
      <span className="art-gallery__card-text">
        <Text.span weight={500} contrast={9} className="art-gallery__card-name">{template.name}</Text.span>
        <Text.caption contrast={6} className="art-gallery__card-by" left={<SourceIcon />}>{byline}</Text.caption>
      </span>
    </button>
    {actions && <Menu placement="bottom-start" items={[
      onEdit && { label: t('editTemplate'), icon: <IconPencil />, onClick: () => onEdit(template) },
      onEdit && onDelete && { divider: true },
      onDelete && { label: t('deleteTemplate'), icon: <IconTrash />, variant: 'error', onClick: () => onDelete(template) },
    ]}>
      <Button className="art-gallery__item-menu" size="small" variant="rounded" color="white" preventLoading icon={<IconDots />} aria-label={t('templateActions', { name: template.name })} />
    </Menu>}
  </div>;
}

// "Editar modelo": the dialog over a personal template, its pages loaded for the preview.
function EditTemplate({ template, fetch, ...props }) {
  const { document } = useTemplateDocument(template, fetch, { eager: true });
  return <TemplateDialog open mode="edit" document={document} initial={template} {...props} />;
}

function DeleteTemplate({ template, onClose, onConfirm }) {
  const { t } = useTranslation('artEditor');
  const [state, setState] = useState(null);
  const confirm = async () => {
    setState('busy');
    try { await onConfirm(template); } catch { setState('error'); }
  };
  return <Modal open size="sm" onClose={onClose} closeLabel={t('close')} title={t('deleteTemplateTitle')} subtitle={t('deleteTemplateSubtitle', { name: template.name })}
    footer={<>
      <Button variant="rounded" color="overlay" preventLoading onClick={onClose}>{t('cancel')}</Button>
      <Button variant="rounded" color="error" loading={state === 'busy'} preventLoading onClick={confirm}>{t('deleteTemplateSubmit')}</Button>
    </>}>
    {state === 'error' && <div className="art-inline-error" role="alert"><Text.caption contrast={8}>{t('deleteTemplateError')}</Text.caption></div>}
  </Modal>;
}

// A row scrolls sideways; its arrows show only when there is more to see.
function Row({ children }) {
  const { t } = useTranslation('artEditor');
  const ref = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const measure = useCallback(() => {
    const element = ref.current;
    if (element) setEdges({ start: element.scrollLeft > 4, end: element.scrollLeft + element.clientWidth < element.scrollWidth - 4 });
  }, []);
  useLayoutEffect(() => {
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [measure]);
  const scroll = direction => ref.current?.scrollBy({ left: direction * ref.current.clientWidth * 0.8, behavior: reducedMotion() ? 'auto' : 'smooth' });
  return <div className="art-gallery__row-wrap">
    <div ref={ref} className="art-gallery__row" onScroll={measure}>{children}</div>
    {edges.start && <Button className="art-gallery__arrow is-start" size="small" variant="rounded" color="current" preventLoading icon={<IconChevronLeft />} aria-label={t('scrollBack')} onClick={() => scroll(-1)} />}
    {edges.end && <Button className="art-gallery__arrow is-end" size="small" variant="rounded" color="current" preventLoading icon={<IconChevronRight />} aria-label={t('scrollMore')} onClick={() => scroll(1)} />}
  </div>;
}

// The Netflix-style preview: after a still hover of 1.5 s on a card, a larger card grows
// out of it (over the others, without moving them), cycles the template's pages and offers
// to use it. Leaving it closes it.
function HoverPreview({ hover, fetch, onPick, onClose, bounds, onEdit, onDelete }) {
  const { t } = useTranslation('artEditor');
  const { template, rect } = hover;
  const { document } = useTemplateDocument(template, fetch, { eager: true });
  const ref = useRef(null);
  const media = useRef(null);
  const [index, setIndex] = useState(0);
  const [top, setTop] = useState(rect.top);
  const pages = document?.pages || [];
  const width = Math.min(Math.max(rect.width * 1.55, 300), 380, bounds.width - 16);
  const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, bounds.width - width - 8));
  const ratio = template.aspect_ratio ? template.aspect_ratio.replace(':', ' / ') : '4 / 5';
  useLayoutEffect(() => {
    const height = ref.current.getBoundingClientRect().height;
    setTop(Math.max(8, Math.min(rect.top + rect.height / 2 - height / 2, bounds.height - height - 8)));
  }, [rect, bounds.height, document]);
  useLayoutEffect(() => {
    const animation = gsap.fromTo(ref.current, { autoAlpha: 0.2, scale: rect.width / width }, { autoAlpha: 1, scale: 1, duration: reducedMotion() ? 0 : 0.24, ease: 'power3.out' });
    return () => animation.kill();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pages.length < 2) return undefined;
    const timer = setInterval(() => setIndex(value => (value + 1) % pages.length), 1800);
    return () => clearInterval(timer);
  }, [pages.length]);
  useLayoutEffect(() => {
    const items = media.current ? [...media.current.querySelectorAll('.art-gallery__hover-page')] : [];
    items.forEach((item, position) => gsap.to(item, { autoAlpha: position === index ? 1 : 0, duration: reducedMotion() ? 0 : 0.35, ease: 'power1.out' }));
  }, [index, pages.length]);
  const preview = template.previews?.[0]?.url;
  return <div ref={ref} className="art-gallery__hover" style={{ left, top, width }} onPointerLeave={event => event.pointerType === 'mouse' && onClose()} role="dialog" aria-label={template.name}>
    <div ref={media} className="art-gallery__hover-media" style={{ aspectRatio: ratio }}>
      {pages.length ? pages.map(page => <div key={page.id} className="art-gallery__hover-page"><ArtPreview document={document} pageId={page.id} rounded={false} /></div>)
        : preview ? <img src={preview} alt="" /> : <span className="art-gallery__thumb-empty art-skeleton" />}
      {pages.length > 1 && <div className="art-gallery__dots" aria-hidden="true">{pages.map((page, position) => <span key={page.id} className={position === index ? 'is-active' : ''} />)}</div>}
    </div>
    <div className="art-gallery__hover-info">
      <Text.h6 weight={500} contrast={9}>{template.name}</Text.h6>
      {template.description && <Text.caption contrast={6}>{template.description}</Text.caption>}
      <Button fullWidth color="inverse" variant="rounded" preventLoading onClick={() => onPick(template)}>{t('useTemplate')}</Button>
      {template.source === 'personal' && (onEdit || onDelete) && <div className="art-gallery__hover-actions">
        {onEdit && <Button size="small" variant="text" color="overlay" preventLoading startIcon={<IconPencil />} onClick={() => onEdit(template)}>{t('editTemplate')}</Button>}
        {onDelete && <Button size="small" variant="text" color="overlay" preventLoading startIcon={<IconTrash />} onClick={() => onDelete(template)}>{t('deleteTemplate')}</Button>}
      </div>}
    </div>
  </div>;
}

// The presets gallery: past designs first (compatible ones lead), then the creator's personal
// templates, then the community and Ginastee templates by category; category chips, a
// search, the format. `source` is the host's data: `templates({ format })`,
// `categories({ format })` and `template(id)` (with its document). When the host keeps
// personal templates it adds `create({ name, description, category_key, tags, document })`
// and `copyImages(document)` (an imported file's images into the account), which put
// "Adicionar modelo" at the end of the chips, and `update(id, fields)` / `remove(id)`, the ⋯
// menu of each personal card. Picking resolves the template's document and hands it to
// `onPick`. `action` sits in the header beside the format (the editor's "start from scratch"
// for a new page).
export default function ArtGallery({ open, onClose, source, onPick, format = 'instagram::post', formats = [], onFormatChange, selectedId = null, title, action = null }) {
  const { t } = useTranslation('artEditor');
  const [state, setState] = useState({ status: 'loading', templates: [], categories: [] });
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [hover, setHover] = useState(null);
  const [picking, setPicking] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const root = useRef(null);
  const timer = useRef(null);
  const fetchTemplate = source?.template;

  const load = useCallback(async () => {
    setState(previous => ({ ...previous, status: 'loading' }));
    try {
      const [categories, page] = await Promise.all([source.categories({ format }), source.templates({ format, per_page: 100 })]);
      setState({ status: 'ready', templates: page.data, categories });
    } catch { setState({ status: 'error', templates: [], categories: [] }); }
  }, [source, format]);
  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const sections = useMemo(() => {
    const words = normalize(query.trim());
    const names = Object.fromEntries(state.categories.map(item => [item.key, item.name]));
    const match = item => !words || [item.name, item.description, names[item.category_key], ...(item.tags || [])].some(value => normalize(value).includes(words));
    const visible = state.templates.filter(match);
    const own = { past: 'past_design', personal: 'personal' };
    if (words || category !== 'all') {
      const list = own[category] ? visible.filter(item => item.source === own[category]) : category === 'all' ? visible : visible.filter(item => item.category_key === category);
      const heading = { past: t('pastDesigns'), personal: t('personalTemplates') };
      return [{ key: 'results', grid: true, title: words ? t('searchResults') : heading[category] || names[category], items: list }];
    }
    const past = visible.filter(item => item.source === 'past_design');
    const mine = visible.filter(item => item.source === 'personal');
    const rows = state.categories.map(item => ({ key: item.key, title: item.name, description: item.description, items: visible.filter(template => !OWN_SOURCES.includes(template.source) && template.category_key === item.key) }))
      .filter(row => row.items.length);
    return [...(past.length ? [{ key: 'past', eyebrow: t('pastDesignsEyebrow'), title: t('pastDesigns'), items: past, icon: IconHistory }] : []),
      ...(mine.length ? [{ key: 'personal', eyebrow: t('personalEyebrow'), title: t('personalTemplates'), items: mine, icon: IconBookmark }] : []), ...rows];
  }, [state, query, category, t]);
  const hasPast = state.templates.some(item => item.source === 'past_design');
  const hasPersonal = state.templates.some(item => item.source === 'personal');
  const formatName = id => formatNameOf(t, id, formats);
  // A new personal template joins the gallery after the past designs (the newest first) and
  // its chip opens, so the creator sees it; an edit replaces it, a delete drops it.
  const addPersonal = template => {
    setImporting(false);
    setState(previous => {
      const past = previous.templates.filter(item => item.source === 'past_design');
      return { ...previous, templates: [...past, template, ...previous.templates.filter(item => item.source !== 'past_design' && item.id !== template.id)] };
    });
    setQuery('');
    setCategory('personal');
    setHover(null);
  };
  const replacePersonal = template => {
    setEditing(null);
    setState(previous => ({ ...previous, templates: previous.templates.map(item => (item.id === template.id ? { ...item, ...template } : item)) }));
  };
  const removePersonal = async template => {
    await source.remove(template.id);
    setDeleting(null);
    if (category === 'personal' && !state.templates.some(item => item.source === 'personal' && item.id !== template.id)) setCategory('all');
    setState(previous => ({ ...previous, templates: previous.templates.filter(item => item.id !== template.id) }));
  };
  const importTemplate = async fields => {
    const document = source.copyImages ? await source.copyImages(fields.document) : fields.document;
    addPersonal(await source.create({ ...fields, document }));
  };
  const cardActions = { onEdit: source?.update ? template => { setHover(null); setEditing(template); } : null,
    onDelete: source?.remove ? template => { setHover(null); setDeleting(template); } : null };

  const startHover = (template, element, now = false) => {
    clearTimeout(timer.current);
    const open = () => {
      const base = root.current.getBoundingClientRect();
      const rect = element.querySelector('.art-gallery__thumb').getBoundingClientRect();
      setHover({ template, rect: { left: rect.left - base.left, top: rect.top - base.top, width: rect.width, height: rect.height } });
    };
    if (now) open(); else timer.current = setTimeout(open, HOVER_DELAY_MS);
  };
  const endHover = () => clearTimeout(timer.current);
  const pick = async template => {
    if (picking) return;
    setPicking(template.id);
    setState(previous => ({ ...previous, pickError: null }));
    try {
      const detail = template.document ? template : await loadTemplate(template, fetchTemplate);
      await onPick({ ...template, ...detail });
    } catch { setState(previous => ({ ...previous, pickError: template.id })); }
    finally { setPicking(null); setHover(null); }
  };
  const body = useRef(null);
  useLayoutEffect(() => { if (open && state.status === 'ready') enter(body.current, { y: 8 }, { duration: 0.22 }); }, [open, state.status, category, query]);

  const bounds = root.current ? { width: root.current.clientWidth, height: root.current.clientHeight } : { width: 1200, height: 800 };
  const current = formats.find(item => item.id === format);
  return <Modal open={open} onClose={onClose} variant="flat" size={1240} className="art-gallery-modal studio-dialog" closeLabel={t('close')}>
    <div ref={root} className="art-gallery" onKeyDown={event => { if (event.key === 'Escape' && hover) { event.stopPropagation(); setHover(null); } }}>
      <header className="art-gallery__header">
        <Text.h3 weight={500} contrast={9}>{title || t('galleryTitle')}</Text.h3>
        <div className="art-gallery__tools">
          {action}
          {current && <Menu placement="bottom-end" items={formats.map(item => ({ label: item.label, active: item.id === format, disabled: !item.available,
            right: item.available ? undefined : <Text.caption contrast={5}>{t('soon')}</Text.caption>, onClick: () => onFormatChange?.(item.id) }))}>
            <Button size="small" variant="rounded" color="overlay" dropdown preventLoading startIcon={<IconLayoutGrid />}>{current.label}</Button>
          </Menu>}
          <label className="art-search is-gallery">
            <IconSearch aria-hidden="true" />
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder={t('gallerySearch')} aria-label={t('gallerySearch')} />
          </label>
        </div>
      </header>
      <div className="art-gallery__filters">
        <div className="art-gallery__chips" role="tablist" aria-label={t('categories')}>
          {[{ key: 'all', name: t('allCategories') }, ...(hasPast ? [{ key: 'past', name: t('pastDesigns') }] : []), ...(hasPersonal ? [{ key: 'personal', name: t('personalTemplates') }] : []),
            ...state.categories].map(item =>
            <Button key={item.key} role="tab" aria-selected={category === item.key} size="small" variant="rounded" color={category === item.key ? 'inverse' : 'overlay'} preventLoading
              onClick={() => { setCategory(item.key); setHover(null); }}>{item.name}</Button>)}
        </div>
        {source?.create && <Button className="art-gallery__add" size="small" variant="rounded" color="overlay" preventLoading startIcon={<IconPlus />} disabled={state.status !== 'ready'}
          onClick={() => { setHover(null); setImporting(true); }}>{t('importTemplate')}</Button>}
      </div>
      {state.pickError && <div className="art-inline-error art-gallery__error" role="alert"><Text.caption contrast={8}>{t('templateLoadError')}</Text.caption></div>}
      <div ref={body} className="art-gallery__body" onScroll={() => hover && setHover(null)}>
        {state.status === 'loading' && <div className="art-gallery__row">{[0, 1, 2, 3, 4, 5].map(index => <span key={index} className="art-gallery__card is-skeleton"><span className="art-gallery__thumb art-skeleton" /></span>)}</div>}
        {state.status === 'error' && <div className="art-inline-error" role="alert"><Text.caption contrast={8}>{t('galleryLoadError')}</Text.caption><Button size="small" variant="text" color="overlay" preventLoading onClick={load}>{t('retry')}</Button></div>}
        {state.status === 'ready' && !sections.some(section => section.items.length) && <div className="art-empty"><Text.caption contrast={6}>{t('galleryEmpty')}</Text.caption></div>}
        {state.status === 'ready' && sections.filter(section => section.items.length).map(section => <section key={section.key} className="art-gallery__section">
          <div className="art-gallery__section-head">
            {section.eyebrow && <Text.subheading contrast={6} left={section.icon ? <section.icon /> : undefined}>{section.eyebrow}</Text.subheading>}
            <Text.h5 weight={500} contrast={9}>{section.title}</Text.h5>
            {section.description && <Text.caption contrast={6}>{section.description}</Text.caption>}
          </div>
          {section.grid
            ? <div className="art-gallery__grid">{section.items.map(template => <Card key={template.id} template={template} fetch={fetchTemplate} selected={template.id === selectedId || picking === template.id}
              onPick={pick} onHover={startHover} onLeave={endHover} {...cardActions} />)}</div>
            : <Row>{section.items.map(template => <Card key={template.id} template={template} fetch={fetchTemplate} selected={template.id === selectedId || picking === template.id}
              onPick={pick} onHover={startHover} onLeave={endHover} {...cardActions} />)}</Row>}
        </section>)}
      </div>
      {hover && <HoverPreview key={hover.template.id} hover={hover} bounds={bounds} fetch={fetchTemplate} onPick={pick} onClose={() => setHover(null)} {...cardActions} />}
    </div>
    {source?.create && <TemplateDialog open={importing} mode="import" onClose={() => setImporting(false)} format={format} formatName={formatName} categories={state.categories}
      category={category} onSubmit={importTemplate} />}
    {editing && <EditTemplate template={editing} fetch={fetchTemplate} onClose={() => setEditing(null)} format={editing.format} formatName={formatName} categories={state.categories}
      onSubmit={async fields => replacePersonal(await source.update(editing.id, { name: fields.name, description: fields.description, category_key: fields.category_key }))} />}
    {deleting && <DeleteTemplate template={deleting} onClose={() => setDeleting(null)} onConfirm={removePersonal} />}
  </Modal>;
}
